import React, { useState } from 'react';
import api from '../../../utils/api';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { useToast } from '../../../hooks/use-toast';
import { downloadBlob } from '../../../utils/blobDownload';

const REPORTS = [
  { id: 'payroll_register', label: 'Payroll Register' },
  { id: 'salary_register', label: 'Salary Register' },
  { id: 'department_wise', label: 'Department-wise Payroll' },
  { id: 'incentive', label: 'Incentive Report' },
  { id: 'deduction', label: 'Deduction Report' },
  { id: 'advance', label: 'Advance Salary Report' },
  { id: 'attendance_vs_payroll', label: 'Attendance vs Payroll' },
  { id: 'employee_cost', label: 'Employee Cost Report' },
];

export default function CrmPayrollReports() {
  const { toast } = useToast();
  const [type, setType] = useState('payroll_register');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [department, setDepartment] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const params = () => {
    const p = new URLSearchParams();
    if (from) p.set('from', from);
    if (to) p.set('to', to);
    if (department) p.set('department', department);
    return p.toString();
  };

  const load = async () => {
    setLoading(true);
    try {
      const q = params();
      const res = await api.get(`/crm/payroll/reports/${type}${q ? `?${q}` : ''}`);
      setRows(res.data?.data?.items || []);
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const exportFile = async (format) => {
    try {
      const q = new URLSearchParams(params());
      q.set('format', format);
      const res = await api.get(`/crm/payroll/reports/${type}?${q.toString()}`, { responseType: 'blob' });
      await downloadBlob(res.data, `${type}.${format === 'excel' ? 'xlsx' : format}`);
    } catch (e) {
      toast({ title: 'Error', description: 'Export failed', variant: 'destructive' });
    }
  };

  const headers = rows[0] ? Object.keys(rows[0]) : [];

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader><CardTitle className="text-base">Payroll Reports</CardTitle></CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <div>
            <Label>Report</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {REPORTS.map((r) => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>From</Label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div>
            <Label>To</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <div>
            <Label>Department</Label>
            <Input value={department} onChange={(e) => setDepartment(e.target.value)} placeholder="Optional" />
          </div>
          <div className="flex items-end gap-2 flex-wrap">
            <Button onClick={load} disabled={loading}>Load</Button>
            <Button variant="outline" onClick={() => exportFile('pdf')}>PDF</Button>
            <Button variant="outline" onClick={() => exportFile('excel')}>Excel</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-4 overflow-x-auto">
          {rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">No rows — load a report</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left">
                  {headers.map((h) => <th key={h} className="py-2 pr-2">{h.replace(/_/g, ' ')}</th>)}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={i} className="border-b">
                    {headers.map((h) => <td key={h} className="py-1 pr-2">{String(row[h] ?? '')}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
